import React, { useEffect, useRef, useState } from 'react';
import type { MenuInputOption } from '@saviynt/design-system';
import {
  Button,
  FooterActionBar,
  Icon,
  MenuInput,
  ModalIsland,
  Typography,
} from '@saviynt/design-system';

import { useInvertedLocalize } from '../../../../../../shared/hooks';
import {
  useGetRulesetsQuery,
  useRunSimulationMutation,
} from '../../../../data-layer/licenseManagementApi';

import './RunStarSimulationModal.css';

export interface RunStarSimulationModalProps {
  isOpen: boolean;
  onClose: () => void;
  vendor: string;
  cloudLicenseKey: number;
  onSimulationSuccess?: () => void;
  onStartSimulation?: (version: string) => void;
}

/**
 * Modal dialog for configuring and launching a STAR simulation.
 * Allows the user to select a ruleset version, set it as default, and start the simulation.
 * @param {RunStarSimulationModalProps} props - Component props
 * @param {boolean} props.isOpen - Whether the modal is visible
 * @param {() => void} props.onClose - Callback invoked when the modal is dismissed
 * @param {(version: string) => void} [props.onStartSimulation] - Optional callback invoked with the selected version when simulation starts
 * @returns {React.ReactElement | null} The rendered modal or null when closed
 */
function RunStarSimulationModal({
  isOpen,
  onClose,
  vendor,
  cloudLicenseKey,
  onSimulationSuccess = undefined,
  onStartSimulation = undefined,
}: RunStarSimulationModalProps): React.ReactElement | null {
  const localize = useInvertedLocalize();
  const [defaultVersion, setDefaultVersion] = useState('');
  const [selectedVersion, setSelectedVersion] = useState('');
  const [isConfirming, setIsConfirming] = useState(false);
  const [hasError, setHasError] = useState(false);
  const defaultVersionRef = useRef(defaultVersion);

  // Fetch rulesets from API
  const {
    data: rulesetOptions = [],
    isLoading: isLoadingRulesets,
    isError: isRulesetsError,
    refetch: refetchRulesets,
  } = useGetRulesetsQuery(
    { vendorType: vendor.toUpperCase() },
    { skip: !isOpen }
  );

  const [runSimulation, { isLoading: isRunning }] = useRunSimulationMutation();

  useEffect(() => {
    defaultVersionRef.current = defaultVersion;
  }, [defaultVersion]);

  // Set default selection when rulesets load
  useEffect(() => {
    if (rulesetOptions.length > 0 && !selectedVersion) {
      setSelectedVersion(rulesetOptions[0].value);
      setDefaultVersion(rulesetOptions[0].value);
    }
  }, [rulesetOptions, selectedVersion]);

  useEffect(() => {
    if (isOpen) {
      setSelectedVersion(defaultVersionRef.current);
      setIsConfirming(false);
      setHasError(false);
    }
  }, [isOpen]);

  if (!isOpen) {
    return null;
  }

  const buildOptions = (): MenuInputOption[] =>
    rulesetOptions.map((r) => ({
      label: r.value === defaultVersion ? `${r.label} (Default)` : r.label,
      value: r.value,
      isChecked: r.value === selectedVersion,
    }));

  const handleVersionChange = (opts: MenuInputOption | undefined): void => {
    const version = opts?.value;

    if (version && version !== selectedVersion) {
      setSelectedVersion(version);
    }
  };

  const handleStartSimulation = (): void => {
    setIsConfirming(true);
    setHasError(false);
  };

  const handleConfirm = (): void => {
    const rulesetKey = Number(selectedVersion);

    runSimulation({
      cloudLicenseKey,
      rulesetKey,
      vendorType: vendor.toUpperCase(),
    })
      .unwrap()
      .then(() => {
        setIsConfirming(false);
        onStartSimulation?.(selectedVersion);
        onSimulationSuccess?.();
        onClose();
      })
      .catch(() => {
        setHasError(true);
      });
  };

  const handleRetry = (): void => {
    setHasError(false);
    handleConfirm();
  };

  const handleGoBack = (): void => {
    setIsConfirming(false);
    setHasError(false);
  };

  const selectedLabel =
    rulesetOptions.find((r) => r.value === selectedVersion)?.label ??
    selectedVersion;
  const triggerLabel =
    selectedVersion === defaultVersion
      ? `${selectedLabel} (Default)`
      : selectedLabel;

  const contentBody = (
    <div className='LicenseManagement-RunStarSimulationModal-content'>
      {isLoadingRulesets && (
        <div className='LicenseManagement-RunStarSimulationModal-loader'>
          <Typography kind='body2'>
            {localize('Loading rulesets...')}
          </Typography>
        </div>
      )}
      {!isLoadingRulesets && isRulesetsError && (
        <div className='LicenseManagement-RunStarSimulationModal-error'>
          <Icon
            kind='AlertWarningFilled'
            size='small'
            className='LicenseManagement-RunStarSimulationModal-errorIcon'
          />
          <Typography kind='body2'>
            {localize('Failed to load rulesets. Please try again.')}
          </Typography>
          <Button kind='outlined' size='small' onClick={refetchRulesets}>
            {localize('Retry')}
          </Button>
        </div>
      )}
      {!isLoadingRulesets && !isRulesetsError && (
        <div className='LicenseManagement-RunStarSimulationModal-row'>
          <Typography
            kind='body2-bold'
            className='LicenseManagement-RunStarSimulationModal-label'>
            {localize('Ruleset version')}
          </Typography>

          <div className='LicenseManagement-RunStarSimulationModal-dropdownWrapper'>
            <MenuInput
              options={buildOptions()}
              selectedOptions={[
                { label: triggerLabel, value: selectedVersion },
              ]}
              onChange={handleVersionChange}
              placeholder={triggerLabel}
              className='LicenseManagement-RunStarSimulationModal-menuInput'
            />
          </div>
        </div>
      )}
    </div>
  );

  const footer = (
    <FooterActionBar
      size='large'
      className='LicenseManagement-RunStarSimulationModal-footer'>
      {isConfirming ? (
        <div className='LicenseManagement-RunStarSimulationModal-confirmStrip'>
          <div className='LicenseManagement-RunStarSimulationModal-confirmMessage'>
            <Icon
              kind={hasError ? 'AlertWarningFilled' : 'AlertWarningFilled'}
              size='small'
              className='LicenseManagement-RunStarSimulationModal-confirmIcon'
            />
            <span className='LicenseManagement-RunStarSimulationModal-confirmText'>
              {hasError
                ? localize(
                    'Simulation failed to start. Please retry or close the modal.'
                  )
                : localize(
                    'This will start a new simulation and may take some time to complete.'
                  )}
            </span>
          </div>
          <div className='LicenseManagement-RunStarSimulationModal-confirmActions'>
            <Button kind='ghost' onClick={hasError ? onClose : handleGoBack}>
              {hasError ? localize('Close') : localize('Go Back')}
            </Button>
            <Button
              kind='filled'
              onClick={hasError ? handleRetry : handleConfirm}
              isDisabled={isRunning}>
              {isRunning
                ? localize('Starting...')
                : localize(hasError ? 'Retry' : 'Confirm')}
            </Button>
          </div>
        </div>
      ) : (
        <div className='LicenseManagement-RunStarSimulationModal-footerActions'>
          <Button kind='ghost' onClick={onClose}>
            {localize('Cancel')}
          </Button>
          <Button kind='filled' onClick={handleStartSimulation}>
            {localize('Start Simulation')}
          </Button>
        </div>
      )}
    </FooterActionBar>
  );

  return (
    <ModalIsland
      isOpen={isOpen}
      onClose={onClose}
      title={localize('Run STAR Simulation')}
      subtitle={localize(
        'Confirm the ruleset to use, then start the simulation'
      )}
      contentBody={contentBody}
      FooterActionBarComp={footer}
      dataTheme='dark'
      overlayBackgroundColor='rgba(128, 128, 128, 0.5)'
      className='LicenseManagement-RunStarSimulationModal'
    />
  );
}

RunStarSimulationModal.defaultProps = {
  onStartSimulation: undefined,
  onSimulationSuccess: undefined,
};

export default RunStarSimulationModal;
